import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm } from '../../utils/helpers';
import BulkPostCreatorModal from './BulkPostCreatorModal';
import MediaPreviewModal from './MediaPreviewModal';
import FolderFormModal from './FolderFormModal';

const MediaLibraryPage = () => {
  const { user, isAdmin, isDesigner } = useAuth();
  const { isRTL, isArabic, t } = useLanguage();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkCreator, setShowBulkCreator] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [folderPath, setFolderPath] = useState([]); // Breadcrumb path
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null); // null = agency-wide media
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    // Reset to page 1 when filter or search changes
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filterType, searchQuery]);

  useEffect(() => {
    clientsAPI.getAll().then((res) => setClients(res.data.clients || [])).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [currentFolderId, selectedClientId]);

  useEffect(() => {
    fetchMedia();
  }, [filterType, searchQuery, pagination.page, currentFolderId, selectedClientId]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (currentFolderId) {
        params.folder_id = currentFolderId;
      } else {
        params.folder_id = 'root'; // Get root level files
      }
      if (filterType !== 'all') {
        params.file_type = filterType;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (selectedClientId) {
        params.client_id = selectedClientId;
      }

      const response = await mediaAPI.getAll(params);
      setMedia(response.data.media || []);
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination,
        }));
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((file) => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/mov', 'video/webm'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length === 0) {
      setError(t('invalidMediaFiles'));
      return;
    }

    if (validFiles.length > 50) {
      setError(t('maxFilesLimit'));
      return;
    }

    try {
      setUploading(true);
      setError(null);

      await mediaAPI.upload(validFiles, currentFolderId, selectedClientId);
      await fetchMedia();
      setSelectedMedia([]);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const handleFolderSelect = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMedia((prev) =>
      prev.includes(mediaId)
        ? prev.filter((id) => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMedia.length === media.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(media.map((m) => m.id));
    }
  };

  const handleDelete = async (mediaId) => {
    const confirmed = await showConfirm(
      t('confirmDeleteMedia'),
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await mediaAPI.delete(mediaId);
      await fetchMedia();
      setSelectedMedia((prev) => prev.filter((id) => id !== mediaId));
      if (previewMedia && previewMedia.id === mediaId) {
        setPreviewMedia(null);
      }
      showToast.success(t('mediaDeletedSuccess'));
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleMediaUpdate = (updatedMedia) => {
    setMedia((prev) =>
      prev.map((m) => (m.id === updatedMedia.id ? updatedMedia : m))
    );
    if (previewMedia && previewMedia.id === updatedMedia.id) {
      setPreviewMedia(updatedMedia);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setSelectedMedia([]); // Clear selection when changing pages
  };

  const fetchFolders = async () => {
    try {
      const params = {};
      if (currentFolderId) {
        params.parent_id = currentFolderId;
      } else {
        params.parent_id = 'root';
      }
      if (selectedClientId) {
        params.client_id = selectedClientId;
      }
      const response = await mediaAPI.getAllFolders(params);
      console.log('Fetched folders:', response.data.folders); // Debug
      setFolders(response.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
      setError(handleApiError(err));
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, folder]);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedMedia([]);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Root
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      // Specific folder
      const newPath = folderPath.slice(0, index + 1);
      setCurrentFolderId(newPath[newPath.length - 1].id);
      setFolderPath(newPath);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedMedia([]);
  };

  const handleCreateFolder = () => {
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleDeleteFolder = async (folderId) => {
    const confirmed = await showConfirm(
      t('confirmDeleteFolder'),
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await mediaAPI.deleteFolder(folderId);
      await fetchFolders();
      if (currentFolderId === folderId) {
        // If we're inside the deleted folder, go back
        handleBreadcrumbClick(-1);
      }
      showToast.success(t('folderDeletedSuccess'));
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  if (loading && media.length === 0) {
    return <Loading fullScreen />;
  }

  const totalMedia = media.length;
  const totalFolders = folders.length;

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header - Gradient Card */}
      <Card className="overflow-hidden border-0 shadow-sm">
        <div className="bg-gradient-to-r from-sky-50 via-white to-emerald-50 px-6 py-5 sm:px-8 sm:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                {t('mediaLibraryTitle')}
              </h1>
              <p className="mt-1 text-sm sm:text-base text-slate-600 max-w-xl">
                {t('mediaLibrarySubtitle')}
              </p>
            </div>

            {/* Quick stats */}
            <div className={`flex flex-wrap gap-3 ${isRTL ? 'md:justify-start' : 'md:justify-end'}`}>
              <div className="px-4 py-2 bg-white/80 rounded-xl shadow-sm">
                <p className="text-xs text-slate-500">
                  {isRTL ? 'الملفات' : 'Media Files'}
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {totalMedia}
                </p>
              </div>
              <div className="px-4 py-2 bg-white/80 rounded-xl shadow-sm">
                <p className="text-xs text-slate-500">
                  {isRTL ? 'المجلدات' : 'Folders'}
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {totalFolders}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Client selector & Header actions */}
        <div className="px-4 sm:px-6 py-4 bg-white border-t border-slate-100 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                {isRTL ? 'العميل:' : 'Client:'}
              </label>
              <select
                value={selectedClientId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedClientId(v || null);
                  setCurrentFolderId(null);
                  setFolderPath([]);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 min-w-[160px]"
              >
                <option value="">{isRTL ? 'عام (الوكالة)' : 'Agency-wide'}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name || c.name || c.id}
                  </option>
                ))}
              </select>
            </div>
            <div className={`flex items-center gap-2 text-xs text-slate-500 ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-600 text-sm">
              📁
            </span>
            <span>
              {isRTL
                ? 'اسحب وافلت الملفات أو استخدم الأزرار لرفع الصور والفيديو'
                : 'Drag & drop files, or use the buttons to upload images and videos.'}
            </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-start md:justify-end">
            {selectedMedia.length > 0 && (
              <Button
                variant="primary"
                onClick={() => setShowBulkCreator(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
              >
                {t('bulkCreatePosts').replace('{count}', selectedMedia.length)}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleCreateFolder}
              className="border-slate-200"
            >
              📁 {t('createFolder')}
            </Button>
            <Button
              variant="outline"
              onClick={() => folderInputRef.current?.click()}
              disabled={uploading}
              className="border-slate-200"
            >
              📂 {t('uploadFolder')}
            </Button>
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              loading={uploading}
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-sm"
            >
              + {t('uploadMedia')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      {(folderPath.length > 0 || currentFolderId) && (
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('home')}
          </button>
          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span className="text-gray-400">/</span>
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Folders Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {folders.length > 0 ? (
          folders.map((folder) => (
            <Card
              key={folder.id}
              className="cursor-pointer hover:shadow-md transition-all group border-slate-200"
              onClick={() => handleFolderClick(folder)}
            >
              <div className="aspect-square bg-gradient-to-br from-sky-50 to-emerald-50 rounded-t-xl flex items-center justify-center">
                <span className="text-5xl">📁</span>
              </div>
              <div className="p-3 space-y-1.5">
                <h3
                  className={`font-semibold text-slate-900 truncate ${isRTL ? 'text-right' : 'text-left'}`}
                  title={folder.name}
                >
                  {folder.name}
                </h3>
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>
                    {folder.media_count || 0} {t('files')}
                  </span>
                  {folder.subfolders_count > 0 && (
                    <span>
                      {folder.subfolders_count} {t('folders')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditFolder(folder);
                    }}
                    className="text-xs text-sky-600 hover:text-sky-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {t('edit')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder.id);
                    }}
                    className="text-xs text-rose-600 hover:text-rose-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {t('delete')}
                  </button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          !currentFolderId && (
            <div className={`col-span-full text-center py-8 text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('noFolders')}
            </div>
          )
        )}
      </div>

      {/* Filters + Search + Select All */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-full">
          {[
            { key: 'all', label: t('allMediaFilter') },
            { key: 'image', label: t('imagesFilter') },
            { key: 'video', label: t('videosFilter') },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilterType(opt.key)}
              className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                filterType === opt.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            placeholder={t('searchMedia')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white ${
              isRTL ? 'text-right' : 'text-left'
            }`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
          {media.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="border-slate-200"
            >
              {selectedMedia.length === media.length
                ? t('clearSelection')
                : t('selectAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-2xl px-6 py-6 sm:px-8 sm:py-8 mt-2
          bg-white shadow-sm transition-all
          ${dragActive ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}
        `}
      >
        {/* Hidden inputs - same logic, فقط تغيير الشكل */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileInputChange}
          className="hidden"
        />
        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFolderSelect}
          className="hidden"
        />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Icon + Text */}
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 shadow-inner">
              <span className="text-3xl">⬆️</span>
            </div>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                {t('dragFilesToUpload')}
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                {t('uploadMediaDescription')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 justify-center md:justify-end">
            <Button
              variant="outline"
              onClick={() => folderInputRef.current?.click()}
              disabled={uploading}
              className="border-slate-200"
            >
              📁 {t('chooseFolder')}
            </Button>
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white"
            >
              📄 {t('chooseFiles')}
            </Button>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      {media.length === 0 ? (
        <Card className="p-12">
          <div className={`text-center ${isRTL ? 'text-right' : 'text-left'}`}>
            <p className="text-gray-500 text-lg mb-4">
              {searchQuery || filterType !== 'all' ? t('noMediaFoundForFilters') : t('noMediaFound')}
            </p>
            {!searchQuery && filterType === 'all' && (
              <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
                {t('uploadYourFirstMedia')}
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {media.map((item) => {
            const isSelected = selectedMedia.includes(item.id);

            return (
              <Card
                key={item.id}
                className={`relative cursor-pointer transition-all group ${
                  isSelected ? 'ring-2 ring-primary-500' : ''
                }`}
                onClick={(e) => {
                  // Double click for preview, single click for selection
                  if (e.detail === 2) {
                    setPreviewMedia(item);
                  } else {
                    toggleMediaSelection(item.id);
                  }
                }}
              >
                {/* Selection Checkbox */}
                <div className={`absolute top-2 z-10 ${isRTL ? 'right-2' : 'left-2'}`}>
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'bg-primary-500 border-primary-500'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Media Preview */}
                <div className="aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
                  {item.file_type === 'image' ? (
                    <img
                      src={getImageUrl(item.file_url)}
                      alt={item.file_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span class="text-4xl">🖼️</span></div>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <span className="text-4xl">🎬</span>
                    </div>
                  )}
                </div>

                {/* Media Info */}
                <div className={`p-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className={`text-xs text-gray-600 truncate ${isRTL ? 'text-right' : 'text-left'}`} title={item.file_name}>
                    {item.file_name}
                  </p>
                  {item.width && item.height && (
                    <p className={`text-xs text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {item.width} × {item.height}
                    </p>
                  )}
                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.tags.slice(0, 2).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-1 py-0.5 bg-primary-100 text-primary-700 rounded text-[10px]"
                        >
                          #{tag}
                        </span>
                      ))}
                      {item.tags.length > 2 && (
                        <span className="text-[10px] text-gray-500">
                          +{item.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {formatDate(item.created_at)}
                  </p>
                </div>

                {/* Preview Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewMedia(item);
                  }}
                  className={`absolute bottom-2 p-1.5 bg-black bg-opacity-50 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity ${isRTL ? 'left-2' : 'right-2'}`}
                  title={t('preview')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  className={`absolute top-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 ${isRTL ? 'left-2' : 'right-2'}`}
                  title={t('delete')}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className={`text-sm text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('showingMedia').replace('{start}', (pagination.page - 1) * pagination.limit + 1).replace('{end}', Math.min(pagination.page * pagination.limit, pagination.total)).replace('{total}', pagination.total)}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              {isRTL ? '← ' : ''}{t('previous')}{!isRTL ? ' ←' : ''}
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      pagination.page === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              {t('next')} {!isRTL ? '→' : '←'}
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Post Creator Modal */}
      {showBulkCreator && (
        <BulkPostCreatorModal
          mediaIds={selectedMedia}
          initialClientId={selectedClientId}
          onClose={() => {
            setShowBulkCreator(false);
            setSelectedMedia([]);
          }}
          onSuccess={() => {
            setShowBulkCreator(false);
            setSelectedMedia([]);
            fetchMedia();
          }}
        />
      )}

      {/* Preview Modal */}
      {previewMedia && (
        <MediaPreviewModal
          media={previewMedia}
          onClose={() => setPreviewMedia(null)}
          onUpdate={handleMediaUpdate}
          onDelete={handleDelete}
        />
      )}

      {/* Folder Form Modal */}
      {showFolderModal && (
        <FolderFormModal
          folder={editingFolder}
          parentId={currentFolderId}
          clientId={selectedClientId}
          onClose={() => {
            setShowFolderModal(false);
            setEditingFolder(null);
          }}
          onSuccess={() => {
            setShowFolderModal(false);
            setEditingFolder(null);
            fetchFolders();
          }}
        />
      )}
    </div>
  );
};

export default MediaLibraryPage;

